import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { getWebApp } from '../telegram/sdk'
import type { ColorScheme } from '../telegram/types'
import { ThemeContext, THEME_STORAGE_KEY } from './themeContext'
import type { ThemeMode } from './themeContext'

function readStoredMode(): ThemeMode {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY)
    if (raw === 'auto' || raw === 'light' || raw === 'dark') return raw
  } catch {
    // localStorage может быть недоступен
  }
  return 'auto'
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode)
  const [autoScheme, setAutoScheme] = useState<ColorScheme>(() => getWebApp().colorScheme)

  useEffect(() => {
    const app = getWebApp()
    const handler = () => setAutoScheme(app.colorScheme)
    app.onEvent('themeChanged', handler)
    return () => app.offEvent('themeChanged', handler)
  }, [])

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => setAutoScheme(mq.matches ? 'dark' : 'light')
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const resolved: ColorScheme = mode === 'auto' ? autoScheme : mode

  useEffect(() => {
    document.documentElement.dataset['theme'] = resolved
  }, [resolved])

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      // не критично
    }
  }, [])

  const value = useMemo(() => ({ mode, resolved, setMode }), [mode, resolved, setMode])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}
